import { initialResources } from '../../game/entities/resources';
import { mockUnits } from '../../game/entities/units';
import { createMapLayout } from '../../game/map/MapLayoutGenerator';
import { createBuildingMetadata, getBuildingDoorAnchor } from '../../game/buildings/BuildingCatalog';
import type { ViewerCommand } from '../commands/CommandBridge';
import type { GameEvent } from '../events/gameEvents';
import { GameplayManager } from '../gameplay/GameplayManager';
import { SeasonManager } from '../seasons/SeasonManager';
import type { Season } from '../seasons/SeasonTypes';
import type { RuntimeActivityEvent, RuntimeBuilding, RuntimeCharacter, RuntimeKingdomResources, RuntimeWorldTime } from '../../game/runtimeConfig';
import type { RoadGraph } from '../../game/worldgen/types';
import type { ActiveEvent, BuildingStage, GameWorldSnapshot, KingdomResourceState, MapData, MapTile, ResourceState, WorldEntity } from './types';

interface Point {
  readonly x: number;
  readonly y: number;
}

interface BuildingEntrance {
  readonly inside: Point;
  readonly outside: Point;
  readonly road: Point;
  readonly approach: readonly Point[];
}

interface NavigationRoadGraph {
  readonly points: readonly Point[];
  readonly byKey: ReadonlyMap<string, Point>;
  readonly adjacency: ReadonlyMap<string, readonly string[]>;
}

interface UnitRoute {
  readonly unitId: string;
  path: readonly Point[];
  waypointIndex: number;
  hiddenUntil: number;
  restarting?: boolean;
  activity: 'walk' | 'carry';
  readonly revealAfterWaypoint?: number;
  readonly hideAfterWaypoint?: number;
  readonly destinationExitPath?: readonly Point[];
  readonly afterRouteDestinationExitPath?: readonly Point[];
  readonly afterRoute?: readonly Point[];
  readonly task?: string;
  readonly workAtDestination?: boolean;
  readonly workInsideDestination?: boolean;
  readonly destinationBuildingId?: string;
  readonly workDurationMs?: number;
  readonly afterRouteWorkInside?: boolean;
  readonly afterRouteDestinationBuildingId?: string;
  readonly afterRouteWorkDurationMs?: number;
  readonly completionRoute?: readonly Point[];
  readonly completionDestinationExitPath?: readonly Point[];
  readonly roaming?: boolean;
  workUntil?: number;
  workingInside?: boolean;
  workedAtDestination?: boolean;
  returning?: boolean;
}

interface GameWorldOptions {
  readonly kingdomId?: string;
  readonly visualPolish?: boolean;
  readonly waterMode?: 'tiles';
  readonly season?: Season;
  readonly worldTime?: RuntimeWorldTime;
  readonly growthLevel?: number;
  readonly characters?: readonly RuntimeCharacter[];
  readonly buildings?: readonly RuntimeBuilding[];
  readonly activityEvents?: readonly RuntimeActivityEvent[];
  readonly resources?: RuntimeKingdomResources;
}

const UNIT_BASE_SPEED_TILES_PER_SECOND = 0.56;
const ROAD_SPEED_MULTIPLIER = 1.25;
const OFF_ROAD_SPEED_MULTIPLIER = 0.5;
const WALK_ANIMATION_RADIANS_PER_MS = 0.0048;

export class GameWorld {
  private readonly layout;
  private readonly seasonManager: SeasonManager;
  private readonly gameplay: GameplayManager;
  private resources: KingdomResourceState = {
    ...initialResources,
    iron: 0,
    clay: 0,
    tools: 0,
    weapons: 0,
    armor: 0,
    siegeEngines: 0,
    bricks: 0,
    buildingMaterials: 0,
    prestige: 0,
    crownCrystals: 0,
    defense: 0,
    satisfaction: 0,
  };
  private buildings: WorldEntity[];
  private readonly generatedBuildings: readonly WorldEntity[];
  private readonly runtimeRoadTiles = new Map<string, MapTile>();
  private readonly buildingEntrances = new Map<string, BuildingEntrance>();
  private runtimeMap?: MapData;
  private units: WorldEntity[] = mockUnits.map((unit) => ({ ...unit }));
  private decorations: WorldEntity[];
  private activeEvents: ActiveEvent[] = [];
  private readonly unitRoutes = new Map<string, UnitRoute>();
  private readonly unitTasks = new Map<string, string>();
  private elapsedMs = 0;
  private currentPhase = 'Aufbau';
  private progressionLevel: number;
  private readonly useVoxelNavigation: boolean;
  private voxelRoadGraph: NavigationRoadGraph;

  constructor(options: GameWorldOptions = {}) {
    this.useVoxelNavigation = Boolean(options.visualPolish);
    this.progressionLevel = Math.max(1, Math.min(6, options.growthLevel ?? 1));
    this.layout = createMapLayout({
      kingdomId: options.kingdomId,
      visualPolish: Boolean(options.visualPolish),
      waterMode: options.waterMode,
      growthLevel: this.progressionLevel,
    });
    this.voxelRoadGraph = sampleRoadGraph(this.layout.roadGraph);
    this.seasonManager = new SeasonManager(options.season, options.worldTime);
    if (options.resources) this.setResources(options.resources);
    this.buildings = this.layout.buildings.map((building) => ({ ...building }));
    this.generatedBuildings = this.buildings.map((building) => ({ ...building }));
    this.decorations = this.layout.decorations.map((decoration) => ({ ...decoration }));
    this.ensureBuildingRoadConnections(this.buildings);
    if (options.buildings?.length) this.setRuntimeBuildings(options.buildings);
	if (options.characters?.length) this.units = this.createCharacterUnits(options.characters);
    this.gameplay = new GameplayManager(this.resources, this.units);
    if (options.activityEvents?.length) this.setActivityEvents(options.activityEvents);
    if (options.characters?.length) {
      this.units.forEach((unit, index) => {
        const character = options.characters?.[index];
        if (!character) return;
        this.unitTasks.set(unit.id, this.characterTaskSignature(character));
        this.assignCharacterTaskRoute(unit, character);
      });
    }
	this.startCharacterMovement();
    for (const message of this.layout.diagnostics) console.info(`[Stream Kingdoms Map] ${message}`);
  }

  setActivityEvents(events: readonly RuntimeActivityEvent[]): void {
    this.activeEvents = events.slice(0, 6).map((event, index) => ({
      id: `activity-${event.createdAt}-${index}`,
      type: event.type,
      title: event.message,
      createdAt: event.createdAt,
    }));
  }

  setResources(resources: RuntimeKingdomResources): void {
    this.resources = { ...resources };
  }

  setRuntimeBuildings(runtimeBuildings: readonly RuntimeBuilding[]): void {
    this.buildingEntrances.clear();
    const desiredHouses = runtimeBuildings.filter((building) => building.kind === 'house').length;
    const runtimeWall = runtimeBuildings.find((building) => building.kind === 'wall');
    const activeKinds = new Set(runtimeBuildings.map((building) => building.kind));
    const fixedBuildings = this.generatedBuildings
      .filter((building) => building.kind !== 'house' && activeKinds.has(building.kind))
      .map((building) => {
        const runtime = runtimeBuildings.find((candidate) => candidate.kind === building.kind);
        const stage = building.kind === 'castle'
          ? this.progressionLevel as BuildingStage
          : Math.max(1, Math.min(4, runtime?.level ?? building.building?.stage ?? 1)) as BuildingStage;
        return {
          ...building,
          label: runtime?.label ?? building.label,
          building: createBuildingMetadata(
            building.kind,
            stage,
            building.building?.variantId ?? 'runtime',
            building.building?.assignedNpc,
          ),
        };
      });
    const houseTemplate = this.generatedBuildings.find((building) => building.kind === 'house');
    const placed: WorldEntity[] = [...fixedBuildings];

    // The database is authoritative. A freshly built structure can exist before
    // its generic growth phase would normally add it to the procedural layout.
    // Place every such runtime building immediately on a free plot and connect
    // its visible entrance to the current road network.
    for (const runtime of runtimeBuildings) {
      if (runtime.kind === 'house' || runtime.kind === 'wall' || placed.some((building) => building.kind === runtime.kind)) continue;
      const metadata = createBuildingMetadata(
        runtime.kind,
        runtime.kind === 'castle'
          ? this.progressionLevel as BuildingStage
          : Math.max(1, Math.min(4, runtime.level)) as BuildingStage,
        'runtime',
      );
      const requested = {
        x: (runtime.x / 100) * Math.max(1, this.layout.map.width - 1),
        y: (runtime.y / 100) * Math.max(1, this.layout.map.height - 1),
      };
      const position = this.findCompletedBuildingPosition(requested, placed, metadata.footprint);
      placed.push({
        id: `runtime-${runtime.kind}`,
        type: 'building',
        kind: runtime.kind,
        x: position.x,
        y: position.y,
        label: runtime.label,
		building: metadata,
      });
    }

    for (let index = 0; index < desiredHouses; index += 1) {
      const position = this.findDynamicHousePosition(placed);
      if (!position) break;
      placed.push({
        ...(houseTemplate ?? {
          type: 'building' as const,
          kind: 'house',
        }),
        id: `runtime-house-${index + 1}`,
        x: position.x,
        y: position.y,
        label: `Haus ${index + 1}`,
      });
    }
    if (runtimeWall) placed.push(...this.createWallPerimeter(placed, runtimeWall.level));
    this.buildings = placed;
	this.removeDecorationsUnderBuildings(this.buildings);
    this.ensureBuildingRoadConnections(this.buildings);
    this.buildingEntrances.clear();
  }

	private removeDecorationsUnderBuildings(buildings: readonly WorldEntity[]): void {
		this.decorations = this.decorations.filter((decoration) => !buildings.some((building) => {
			const width = building.building?.footprint.width ?? 2;
			const height = building.building?.footprint.height ?? 2;
			return Math.abs(decoration.x - building.x) < Math.max(1.35, width * 0.72)
				&& Math.abs(decoration.y - building.y) < Math.max(1.2, height * 0.65);
		}));
	}

  /**
   * Builds a complete perimeter from the current settlement footprint.
   * The result is data-driven: every generated scenario gets its own bounds,
   * segment count, gate position and orientation.
   */
  private createWallPerimeter(buildings: readonly WorldEntity[], wallLevel: number): readonly WorldEntity[] {
    const coreKinds = new Set([
      'castle', 'warehouse', 'market', 'house', 'mill', 'bakery', 'smithy',
      'barracks', 'stable', 'mage_tower', 'smelter', 'brickyard', 'builder_yard',
    ]);
    const anchors = buildings
      .filter((building) => coreKinds.has(building.kind))
      .map((building) => this.navigationPoint(building));
    if (!anchors.length) anchors.push({ x: this.layout.map.width / 2, y: this.layout.map.height / 2 });

    const minX = Math.min(...anchors.map((point) => point.x));
    const maxX = Math.max(...anchors.map((point) => point.x));
    const minY = Math.min(...anchors.map((point) => point.y));
    const maxY = Math.max(...anchors.map((point) => point.y));
    const horizontalMargin = 3.2 + wallLevel * 0.1;
    const verticalMargin = 2.6 + wallLevel * 0.08;
    const left = Math.max(0.6, minX - horizontalMargin);
    const right = Math.min(this.layout.map.width - 0.6, maxX + horizontalMargin);
    const top = Math.max(0.6, minY - verticalMargin);
    const bottom = Math.min(this.layout.map.height - 0.6, maxY + verticalMargin);
    const center = { x: (left + right) / 2, y: (top + bottom) / 2 };
    const vertices = [
      { x: left, y: top },
      { x: right, y: top },
      { x: right, y: bottom },
      { x: left, y: bottom },
    ];

    const segments: WorldEntity[] = [];
    for (let edgeIndex = 0; edgeIndex < vertices.length; edgeIndex += 1) {
      const from = vertices[edgeIndex];
      const to = vertices[(edgeIndex + 1) % vertices.length];
      const horizontal = Math.abs(to.x - from.x) > Math.abs(to.y - from.y);
      const distance = Math.hypot(to.x - from.x, to.y - from.y);
      const steps = Math.max(2, Math.ceil(distance / (horizontal ? 2.55 : 1.65)));
      const kind = horizontal
        ? 'wall_straight_horizontal'
        : 'wall_straight_vertical';
      for (let step = 1; step < steps; step += 1) {
        const progress = step / steps;
        segments.push({
          id: `perimeter-wall-${edgeIndex}-${step}`,
          type: 'building',
          kind,
          x: from.x + (to.x - from.x) * progress,
          y: from.y + (to.y - from.y) * progress,
          facing: edgeIndex >= 2 ? 'west' : 'east',
        });
      }
    }

    const roads = this.useVoxelNavigation
      ? this.voxelRoadPoints()
      : this.currentMap().tiles.filter((tile) => this.isRoadTile(tile));
    const gate = segments.reduce((best, segment) => {
      const roadDistance = roads.reduce((distance, road) =>
        Math.min(distance, Math.hypot(road.x - segment.x, road.y - segment.y)),
      Number.POSITIVE_INFINITY);
      const verticalSegmentPenalty = segment.kind === 'wall_straight_vertical' ? 20 : 0;
      const northPenalty = segment.y < center.y ? 2.5 : 0;
      const score = roadDistance + verticalSegmentPenalty + northPenalty;
      return !best || score < best.score ? { segment, score } : best;
    }, undefined as { segment: WorldEntity; score: number } | undefined);
    const gateId = gate?.segment.id;
    const orientedSegments = segments.map((segment): WorldEntity =>
      segment.id === gateId ? { ...segment, kind: 'wall_gate' } : segment,
    );

    return [
      ...orientedSegments,
      ...vertices.map((point, index): WorldEntity => ({
        id: `perimeter-tower-${index}`,
        type: 'building',
        kind: 'wall_tower',
        x: point.x,
        y: point.y,
      })),
    ];
  }

  private findDynamicHousePosition(existing: readonly WorldEntity[]): Point | undefined {
    const map = this.currentMap();
    const roads = map.tiles.filter((tile) => this.isRoadTile(tile));
    const tiles = new Map(map.tiles.map((tile) => [`${tile.x}:${tile.y}`, tile]));
    const candidates = map.tiles
      .filter((tile) => tile.type === 'grass')
      .filter((tile) => roads.some((road) => Math.abs(road.x - tile.x) + Math.abs(road.y - tile.y) <= 3))
      .filter((tile) => {
        for (let y = tile.y - 1; y <= tile.y + 1; y += 1) {
          for (let x = tile.x - 1; x <= tile.x + 1; x += 1) {
            if (tiles.get(`${x}:${y}`)?.type !== 'grass') return false;
          }
        }
        return true;
      })
      .sort((a, b) => {
        const aCenter = Math.hypot(a.x - 16, a.y - 10);
        const bCenter = Math.hypot(b.x - 16, b.y - 10);
        return aCenter - bCenter || a.y - b.y || a.x - b.x;
      });
    return candidates.find((candidate) => {
      const overlapsBuilding = existing.some((building) =>
        Math.abs(building.x - candidate.x) < 1.9 && Math.abs(building.y - candidate.y) < 1.45,
      );
      const overlapsDecoration = this.decorations.some((decoration) =>
        Math.abs(decoration.x - candidate.x) < 0.9 && Math.abs(decoration.y - candidate.y) < 0.85,
      );
      return !overlapsBuilding && !overlapsDecoration;
    });
  }

  setCharacters(characters: readonly RuntimeCharacter[]): void {
    if (!characters.length) return;
	const previousById = new Map(this.units.map((unit) => [unit.id, unit]));
	const claimedPreviousIds = new Set<string>();
	this.units = this.createCharacterUnits(characters).map((unit) => {
		const exact = previousById.get(unit.id);
		const existing = exact ?? this.units.find((candidate) =>
			candidate.label === unit.label && !claimedPreviousIds.has(candidate.id),
		);
		if (existing) claimedPreviousIds.add(existing.id);
		return existing
			? {
				...unit,
				x: existing.x,
				y: existing.y,
				facing: existing.facing,
				activity: existing.activity,
				animationPhase: existing.animationPhase,
				hidden: existing.hidden,
				roadOffsetX: existing.roadOffsetX,
				roadOffsetY: existing.roadOffsetY,
			}
			: unit;
	});
	const activeIds = new Set(this.units.map((unit) => unit.id));
	const charactersByUnitId = new Map(this.createCharacterUnits(characters).map((unit, index) => [unit.id, characters[index]]));
	for (const unitId of this.unitRoutes.keys()) {
		if (!activeIds.has(unitId)) this.unitRoutes.delete(unitId);
	}
	for (const unit of this.units) {
		const character = charactersByUnitId.get(unit.id);
		if (!character) continue;
		const taskSignature = this.characterTaskSignature(character);
		if (this.unitTasks.get(unit.id) === taskSignature) continue;
		this.unitTasks.set(unit.id, taskSignature);
		this.unitRoutes.delete(unit.id);
		this.assignCharacterTaskRoute(unit, character);
	}
	this.startCharacterMovement();
  }

  private createCharacterUnits(characters: readonly RuntimeCharacter[]): WorldEntity[] {
    return characters.map((character, index) => {
      const requestedPosition = {
        x: Math.max(1, Math.min(30, Math.round((character.x / 100) * 31))),
        y: Math.max(1, Math.min(16, Math.round((character.y / 100) * 17))),
      };
      const roadPosition = this.nearestRoad(requestedPosition);
      return {
        id: `live-character-${index}-${character.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
        type: 'unit',
        kind: this.characterKind(character.profession),
        x: roadPosition.x,
        y: roadPosition.y,
        label: character.name,
        appearance: {
          hairColor: character.hairColor,
          shirtColor: character.shirtColor,
          pantsColor: character.pantsColor,
		  gender: character.gender,
		  isStreamer: character.isStreamer,
        },
      };
    });
  }

  private characterTaskSignature(character: RuntimeCharacter): string {
    return `${character.task}:${character.targetX}:${character.targetY}:${character.taskStartedAt}`;
  }

  private characterKind(profession: string): string {
    if (profession === 'holzfaeller') return 'woodcutter';
    if (profession === 'haendler') return 'trader';
    if (['soldat', 'waechter', 'ritter'].includes(profession)) return 'knight';
    return 'worker';
  }

  update(deltaMs: number): void {
    this.elapsedMs += deltaMs;
    this.seasonManager.update(deltaMs);
    this.updateUnitRoutes(deltaMs);
    this.updateUnitAvoidance(deltaMs);
    this.gameplay.update(deltaMs, this.seasonManager.getState(), this.buildings, this.progressionLevel);
    this.currentPhase = this.gameplay.getSnapshot().phase;
  }

  getSnapshot(): GameWorldSnapshot {
    return {
      map: this.currentMap(),
      resources: { ...this.resources },
      gameplay: this.gameplay.getSnapshot(),
      season: this.seasonManager.getState(),
      buildings: this.buildings.map((building) => ({ ...building })),
      units: this.units.map((unit) => ({ ...unit })),
      decorations: this.decorations.map((decoration) => ({ ...decoration })),
      activeEvents: this.activeEvents.map((event) => ({ ...event })),
      currentPhase: this.currentPhase,
      progressionLevel: this.progressionLevel,
    };
  }

  getNavigationDebug(): {
    readonly roadPointCount: number;
    readonly units: readonly {
      readonly id: string;
      readonly centerlineDistance: number;
      readonly avoidanceOffset: number;
      readonly onRoad: boolean;
    }[];
  } {
    const roads = this.voxelRoadPoints();
    return {
      roadPointCount: roads.length,
      units: this.units.map((unit) => {
        const rendered = {
          x: unit.x + (unit.roadOffsetX ?? 0),
          y: unit.y + (unit.roadOffsetY ?? 0),
        };
        const centerlineDistance = roads.reduce((best, road) =>
          Math.min(best, Math.hypot(road.x - rendered.x, road.y - rendered.y)),
        Number.POSITIVE_INFINITY);
        return {
          id: unit.id,
          centerlineDistance: Math.round(centerlineDistance * 1_000) / 1_000,
          avoidanceOffset: Math.round(Math.hypot(unit.roadOffsetX ?? 0, unit.roadOffsetY ?? 0) * 1_000) / 1_000,
          onRoad: this.isRoadPosition(unit),
        };
      }),
    };
  }

  applyEvent(event: GameEvent): void {
    this.activeEvents.unshift({
      id: event.id,
      type: event.type,
      title: event.title,
      createdAt: event.timestamp,
    });
    this.activeEvents.length = Math.min(this.activeEvents.length, 6);
    const gameplayResult = this.gameplay.applyEvent(event, this.seasonManager.getState());
    if (gameplayResult) {
      this.activeEvents.unshift({
        id: `${event.id}-gameplay`,
        type: gameplayResult.cameraHint ?? event.type,
        title: gameplayResult.title,
        createdAt: event.timestamp,
      });
      this.activeEvents.length = Math.min(this.activeEvents.length, 6);
    }

    switch (event.type) {
      case 'viewer_joined':
        const spawnRoad = this.nearestRoad({
          x: 14 + (this.units.length % 4),
          y: 10 + (this.units.length % 3),
        });
        const joinedUnit: WorldEntity = {
          id: `viewer-${event.viewerName.toLowerCase()}`,
          type: 'unit',
          kind: 'worker',
          x: spawnRoad.x,
          y: spawnRoad.y,
          label: event.viewerName,
        };
        this.units.push(joinedUnit);
        this.assignWorkRoute(joinedUnit, event.viewerName);
        break;
      case 'resource_collected':
        this.resources[event.resource] += Math.round(event.amount * this.resourceModifier(event.resource));
        this.moveUnit(event.viewerName);
        break;
      case 'building_completed':
        if (event.kind === 'wall') {
          this.buildings = this.buildings.filter((building) => !building.kind.startsWith('wall_'));
          this.buildings.push(...this.createWallPerimeter(this.buildings, 1));
          this.buildingEntrances.clear();
          break;
        }
        const completedBuilding: WorldEntity = {
          id: event.buildingId,
          type: 'building',
          kind: event.kind,
          ...this.findCompletedBuildingPosition({ x: event.x, y: event.y }),
          label: event.kind,
        };
        this.buildings.push(completedBuilding);
        this.ensureBuildingRoadConnections([completedBuilding]);
        this.buildingEntrances.clear();
        break;
      case 'village_level_up':
        this.progressionLevel = event.level;
        this.buildingEntrances.clear();
        this.currentPhase = 'Wachstum';
        break;
      default:
        break;
    }
  }

  applyCommand(command: ViewerCommand): ActiveEvent {
    const result = this.gameplay.handleCommand(command.command, command.viewerName, command.createdAt, this.seasonManager.getState());
    const event: ActiveEvent = {
      id: command.id,
      type: result.cameraHint ?? 'viewer_command',
      title: result.title,
      createdAt: command.createdAt,
    };
    this.activeEvents.unshift(event);
    this.activeEvents.length = Math.min(this.activeEvents.length, 6);
    return event;
  }

  private moveUnit(viewerName: string): void {
    const unit = this.units.find((candidate) => candidate.label === viewerName);
    if (!unit) return;
    this.assignWorkRoute(unit, viewerName);
  }

  private assignWorkRoute(unit: WorldEntity, viewerName: string, startExitPath?: readonly Point[]): void {
    const visibleVoxelWorkKinds = ['woodcutter_camp', 'mine', 'farm', 'market'];
    const availableWorkBuildings = this.buildings.filter((building) =>
      (this.useVoxelNavigation
        ? visibleVoxelWorkKinds
        : ['woodcutter_camp', 'mine', 'farm', 'market', 'smithy', 'bakery']
      ).includes(building.kind),
    );
    const startRoad = this.nearestRoad(unit);
    const reachableWork = availableWorkBuildings
      .map((building) => {
        const road = this.nearestRoad(building);
        return { building, road, route: this.findRoadRoute(startRoad, road) };
      })
      .filter((candidate) => candidate.route.length > 0);
    if (!reachableWork.length) {
      this.assignRoadRoam(unit);
      return;
    }

    const resourceIndex = Math.abs(viewerName.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0)) % reachableWork.length;
    const selectedWork = reachableWork[resourceIndex];
    const workBuilding = selectedWork.building;
    const workRoad = selectedWork.road;
    const workRoute = this.appendBuildingEntrance(selectedWork.route, workBuilding, workRoad);
    if (workRoute.length < 2) return;

    const storeBuilding = this.buildings.find((building) => building.kind === 'warehouse');
    const storeRoad = storeBuilding ? this.nearestRoad(storeBuilding) : undefined;
    const storeRoadRoute = storeRoad ? this.findRoadRoute(workRoad, storeRoad) : [];
    const storeRoute = storeBuilding && storeRoad && storeRoadRoute.length > 0
      ? this.appendBuildingEntrance(storeRoadRoute, storeBuilding, storeRoad)
      : [];
    const workPath = startExitPath?.length
      ? this.pathWithBuildingExit(unit, startExitPath, workRoute)
      : this.pathWithCurrentPosition(unit, workRoute);
    unit.hidden = Boolean(startExitPath?.length);
    this.unitRoutes.set(unit.id, {
      unitId: unit.id,
      path: workPath,
      waypointIndex: 0,
      hiddenUntil: 0,
      activity: 'walk',
      revealAfterWaypoint: startExitPath?.length ? 1 : undefined,
      hideAfterWaypoint: Math.max(0, workPath.length - 2),
      destinationExitPath: this.buildingExitPath(workBuilding, workRoad),
      workInsideDestination: true,
      destinationBuildingId: workBuilding.id,
      workDurationMs: this.workDurationForTask(workBuilding.kind, true),
      afterRouteDestinationExitPath: storeBuilding && storeRoad && storeRoute.length > 1
        ? this.buildingExitPath(storeBuilding, storeRoad)
        : undefined,
      afterRoute: storeRoute.length > 1 ? storeRoute : undefined,
    });
  }

  private assignRoadRoam(unit: WorldEntity): void {
    const startRoad = this.nearestRoad(unit);
    const destinations = this.navigationRoadPoints()
      .map((road) => ({ road, route: this.findRoadRoute(startRoad, road) }))
      .filter((candidate) => candidate.route.length > 1)
      .sort((a, b) =>
        Math.hypot(b.road.x - startRoad.x, b.road.y - startRoad.y)
        - Math.hypot(a.road.x - startRoad.x, a.road.y - startRoad.y),
      );
    const selected = destinations[0];
    if (!selected) {
      unit.hidden = false;
      unit.activity = 'idle';
      return;
    }
    unit.hidden = false;
    this.unitRoutes.set(unit.id, {
      unitId: unit.id,
      path: this.pathWithCurrentPosition(unit, selected.route),
      waypointIndex: 0,
      hiddenUntil: 0,
      activity: 'walk',
      roaming: true,
    });
  }

	private startCharacterMovement(): void {
		for (const unit of this.units) {
			if (this.unitRoutes.has(unit.id)) continue;
			const startRoad = this.nearestRoad(unit);
			unit.x = startRoad.x;
			unit.y = startRoad.y;
			this.assignWorkRoute(unit, unit.label ?? unit.id);
		}
	}

  private assignCharacterTaskRoute(unit: WorldEntity, character: RuntimeCharacter): void {
    const task = character.task || 'idle';
    if (task === 'idle') {
      this.assignWorkRoute(unit, unit.label ?? unit.id);
      return;
    }

    const target = this.findTaskTarget(task, character.targetX, character.targetY);
    if (!target) {
      this.assignWorkRoute(unit, unit.label ?? unit.id);
      return;
    }

    const warehouse = this.buildings.find((building) => building.kind === 'warehouse');
    const productionTasks = new Set([
      'schmiede', 'werkzeug', 'waffen', 'ruestung', 'belagerung', 'ziegel',
      'baumaterial', 'reparatur', 'bauen', 'strasse', 'muhle', 'backerei',
      'stall', 'wachturm',
    ]);

    // Verarbeitung beginnt sichtbar am Lager: Material aufnehmen und zur
    // jeweiligen Produktionsstätte bringen.
    if (warehouse && target.type === 'building' && productionTasks.has(task) && target.id !== warehouse.id) {
      this.assignBuildingChainRoute(unit, warehouse, target, task);
      return;
    }

    const startRoad = this.nearestRoad(unit);
    const targetRoad = this.nearestRoad(target);
    const roadRoute = this.findRoadRoute(startRoad, targetRoad);
    if (!roadRoute.length) {
      this.assignWorkRoute(unit, unit.label ?? unit.id);
      return;
    }

    const isBuilding = target.type === 'building';
    const destinationRoute = isBuilding
      ? this.appendBuildingEntrance(roadRoute, target, targetRoad)
      : this.appendResourceApproach(roadRoute, target);
    const path = this.pathWithCurrentPosition(unit, destinationRoute);
    const warehouseRoad = warehouse ? this.nearestRoad(warehouse) : undefined;
    const warehouseRoadRoute = warehouse && warehouseRoad ? this.findRoadRoute(targetRoad, warehouseRoad) : [];
    const warehouseRoute = warehouse && warehouseRoad && warehouseRoadRoute.length
      ? this.appendBuildingEntrance(warehouseRoadRoute, warehouse, warehouseRoad)
      : undefined;
    this.unitRoutes.set(unit.id, {
      unitId: unit.id,
      path,
      waypointIndex: 0,
      hiddenUntil: 0,
      activity: 'walk',
      task,
      workAtDestination: !isBuilding,
      workInsideDestination: isBuilding,
      destinationBuildingId: isBuilding ? target.id : undefined,
      workDurationMs: this.workDurationForTask(task, isBuilding),
      hideAfterWaypoint: isBuilding ? Math.max(0, path.length - 2) : undefined,
      destinationExitPath: isBuilding ? this.buildingExitPath(target, targetRoad) : [targetRoad],
      afterRoute: warehouseRoute,
      afterRouteDestinationExitPath: warehouse && warehouseRoad ? this.buildingExitPath(warehouse, warehouseRoad) : undefined,
    });
  }

  private assignBuildingChainRoute(unit: WorldEntity, source: WorldEntity, destination: WorldEntity, task: string): void {
    const startRoad = this.nearestRoad(unit);
    const sourceRoad = this.nearestRoad(source);
    const sourceRoadRoute = this.findRoadRoute(startRoad, sourceRoad);
    const destinationRoad = this.nearestRoad(destination);
    const destinationRoadRoute = this.findRoadRoute(sourceRoad, destinationRoad);
    const returnRoadRoute = this.findRoadRoute(destinationRoad, sourceRoad);
    if (!sourceRoadRoute.length || !destinationRoadRoute.length) {
      this.assignWorkRoute(unit, unit.label ?? unit.id);
      return;
    }
    const sourceRoute = this.appendBuildingEntrance(sourceRoadRoute, source, sourceRoad);
    const destinationRoute = this.appendBuildingEntrance(destinationRoadRoute, destination, destinationRoad);
    const returnRoute = returnRoadRoute.length
      ? this.appendBuildingEntrance(returnRoadRoute, source, sourceRoad)
      : undefined;
    const path = this.pathWithCurrentPosition(unit, sourceRoute);
    this.unitRoutes.set(unit.id, {
      unitId: unit.id,
      path,
      waypointIndex: 0,
      hiddenUntil: 0,
      activity: 'walk',
      task,
      hideAfterWaypoint: Math.max(0, path.length - 2),
      destinationExitPath: this.buildingExitPath(source, sourceRoad),
      afterRoute: destinationRoute,
      afterRouteDestinationExitPath: this.buildingExitPath(destination, destinationRoad),
      afterRouteWorkInside: true,
      afterRouteDestinationBuildingId: destination.id,
      afterRouteWorkDurationMs: this.workDurationForTask(task, true),
      completionRoute: returnRoute,
      completionDestinationExitPath: this.buildingExitPath(source, sourceRoad),
    });
  }

  private workDurationForTask(task: string, indoors: boolean): number {
    const normalized = task.toLowerCase();
    if (['holz', 'wood', 'stein', 'stone', 'lehm', 'clay', 'mine', 'eisen', 'iron'].includes(normalized)) {
      return 8_000;
    }
    if (['schmiede', 'werkzeug', 'waffen', 'ruestung', 'belagerung', 'ziegel', 'muhle', 'backerei'].includes(normalized)) {
      return 9_000;
    }
    if (['farm', 'versorgung', 'land'].includes(normalized)) return 7_500;
    return indoors ? 7_200 : 6_500;
  }

  private findTaskTarget(task: string, targetX: number, targetY: number): WorldEntity | undefined {
    const taskKinds: Record<string, readonly string[]> = {
      holz: ['tree_oak', 'tree_pine'],
      wood: ['tree_oak', 'tree_pine'],
      stein: ['rocks'],
      stone: ['rocks'],
      farm: ['farm'],
      versorgung: ['farm'],
      mine: ['mine'],
      eisen: ['mine'],
      iron: ['mine'],
      lehm: ['rocks'],
      clay: ['rocks'],
      schmiede: ['smithy'],
      werkzeug: ['smithy'],
      waffen: ['smithy'],
      ruestung: ['smithy'],
      belagerung: ['smithy'],
      ziegel: ['smithy'],
      baumaterial: ['warehouse'],
      reparatur: ['castle'],
      bauen: ['castle'],
      strasse: ['market'],
      land: ['farm'],
      muhle: ['mill'],
      backerei: ['bakery'],
      stall: ['market'],
      wachturm: ['watchtower'],
      attack: ['watchtower'],
      defend: ['castle'],
      heal: ['house'],
      spy: ['watchtower'],
      sabotage: ['watchtower'],
    };
    const kinds = taskKinds[task] ?? [];
    if (!kinds.length) return undefined;
    const candidates = [...this.decorations, ...this.buildings].filter((entity) => kinds.includes(entity.kind));
    if (!candidates.length) return undefined;
    const requested = {
      x: Math.max(1, Math.min(32, (targetX / 100) * 33)),
      y: Math.max(1, Math.min(18, (targetY / 100) * 19)),
    };
    return candidates.reduce((best, candidate) =>
      Math.hypot(candidate.x - requested.x, candidate.y - requested.y) <
      Math.hypot(best.x - requested.x, best.y - requested.y) ? candidate : best,
    );
  }

  private appendResourceApproach(route: readonly Point[], resource: WorldEntity): readonly Point[] {
    const last = route[route.length - 1];
    if (!last) return route;
    const resourcePoint = this.navigationPoint(resource);
    const dx = resourcePoint.x - last.x;
    const dy = resourcePoint.y - last.y;
    const distance = Math.hypot(dx, dy);
    if (distance < 0.05) return route;
    // Die Figur bleibt knapp vor dem Objekt stehen, statt den Baum oder Felsen
    // zu überdecken. Nur der kurze letzte Schritt führt vom Weg zum Arbeitsplatz.
    const stopDistance = Math.min(0.58, Math.max(0.24, distance * 0.35));
    return [...route, {
      x: resourcePoint.x - (dx / distance) * stopDistance,
      y: resourcePoint.y - (dy / distance) * stopDistance,
    }];
  }

  private updateUnitRoutes(deltaMs: number): void {
    const baseSpeed = UNIT_BASE_SPEED_TILES_PER_SECOND
      * (this.seasonManager.getState().production.movement || 1);
    for (const [unitId, route] of this.unitRoutes) {
      const unit = this.units.find((candidate) => candidate.id === unitId);
      if (!unit) {
        this.unitRoutes.delete(unitId);
        continue;
      }

      if (route.restarting) {
        unit.hidden = true;
        if (route.hiddenUntil > this.elapsedMs) continue;
        this.unitRoutes.delete(unitId);
        unit.activity = 'idle';
        if (route.task && this.unitTasks.has(unitId)) {
          this.reassignStoredTask(unit);
        } else {
          this.assignWorkRoute(unit, unit.label ?? unit.id, route.destinationExitPath);
        }
        continue;
      }

      if (route.workingInside) {
        unit.hidden = true;
        unit.activity = 'work';
        if ((route.workUntil ?? 0) > this.elapsedMs) continue;
        route.workingInside = false;
        route.workedAtDestination = true;
        route.workUntil = undefined;
      }

      if (route.workAtDestination && route.workUntil !== undefined) {
        unit.hidden = false;
        unit.activity = 'work';
        unit.animationPhase = ((unit.animationPhase ?? 0) + deltaMs * 0.012) % (Math.PI * 2);
        if (route.workUntil > this.elapsedMs) continue;
        if (route.afterRoute?.length) {
          const deliveryPath = route.destinationExitPath?.length
            ? this.pathWithBuildingExit(unit, route.destinationExitPath, route.afterRoute)
            : this.pathWithCurrentPosition(unit, route.afterRoute);
          this.unitRoutes.set(unitId, {
            unitId,
            path: deliveryPath,
            waypointIndex: 0,
            hiddenUntil: 0,
            activity: 'carry',
            hideAfterWaypoint: Math.max(0, deliveryPath.length - 2),
            destinationExitPath: route.afterRouteDestinationExitPath,
            task: route.task,
            workInsideDestination: route.afterRouteWorkInside,
            destinationBuildingId: route.afterRouteDestinationBuildingId,
            workDurationMs: route.afterRouteWorkDurationMs,
            afterRoute: route.completionRoute,
            afterRouteDestinationExitPath: route.completionDestinationExitPath,
          });
          continue;
        } else {
          const returnPath = [...route.path].reverse();
          route.path = returnPath;
          route.waypointIndex = 0;
          route.workUntil = undefined;
          route.returning = true;
          unit.activity = 'carry';
        }
      }

      if (route.hiddenUntil > this.elapsedMs) {
        unit.hidden = true;
        continue;
      }
      const concealingExit = route.revealAfterWaypoint !== undefined && route.waypointIndex < route.revealAfterWaypoint;
      const concealingEntry = route.hideAfterWaypoint !== undefined && route.waypointIndex >= route.hideAfterWaypoint;
      unit.hidden = concealingExit || concealingEntry;
      unit.animationPhase = (
        (unit.animationPhase ?? 0) + deltaMs * WALK_ANIMATION_RADIANS_PER_MS
      ) % (Math.PI * 2);

      const target = route.path[route.waypointIndex + 1];
      if (!target) {
        if (route.roaming) {
          route.path = [...route.path].reverse();
          route.waypointIndex = 0;
          unit.hidden = false;
          unit.activity = 'walk';
          continue;
        }
        if (route.workAtDestination && !route.returning) {
          unit.hidden = false;
          unit.activity = 'work';
          route.workUntil = this.elapsedMs + (route.workDurationMs ?? 6_500);
          continue;
        }
        if (route.workInsideDestination && !route.workedAtDestination) {
          unit.hidden = true;
          unit.activity = 'work';
          route.workingInside = true;
          route.workUntil = this.elapsedMs + (route.workDurationMs ?? 7_200);
          continue;
        }
        if (route.workAtDestination && route.returning) {
          this.unitRoutes.delete(unitId);
          unit.hidden = false;
          unit.activity = 'idle';
          this.reassignStoredTask(unit);
          continue;
        }
        if (route.afterRoute && route.afterRoute.length > 1) {
          unit.hidden = true;
          unit.activity = 'enter';
          const nextPath = route.destinationExitPath?.length
            ? this.pathWithBuildingExit(unit, route.destinationExitPath, route.afterRoute)
            : this.pathWithCurrentPosition(unit, route.afterRoute);
          this.unitRoutes.set(unitId, {
            unitId,
            path: nextPath,
            waypointIndex: 0,
            hiddenUntil: this.elapsedMs + 380,
            activity: 'carry',
            revealAfterWaypoint: route.destinationExitPath?.length ? 1 : undefined,
            hideAfterWaypoint: Math.max(0, nextPath.length - 2),
            destinationExitPath: route.afterRouteDestinationExitPath,
            task: route.task,
            workInsideDestination: route.afterRouteWorkInside,
            destinationBuildingId: route.afterRouteDestinationBuildingId,
            workDurationMs: route.afterRouteWorkDurationMs,
            afterRoute: route.completionRoute,
            afterRouteDestinationExitPath: route.completionDestinationExitPath,
          });
        } else {
          unit.hidden = true;
          unit.activity = 'enter';
          route.hiddenUntil = this.elapsedMs + 380;
          route.restarting = true;
        }
        continue;
      }

      const dx = target.x - unit.x;
      const dy = target.y - unit.y;
      const distance = Math.hypot(dx, dy);
      const surfaceMultiplier = this.isRoadPosition(unit)
        ? ROAD_SPEED_MULTIPLIER
        : OFF_ROAD_SPEED_MULTIPLIER;
      const step = (deltaMs / 1000) * baseSpeed * surfaceMultiplier;
      if (distance <= step || distance < 0.01) {
        unit.x = target.x;
        unit.y = target.y;
        route.waypointIndex += 1;
        unit.activity = route.activity;
      } else {
        unit.facing = resolveFacing(dx, dy);
        unit.activity = route.activity;
        unit.x += (dx / distance) * step;
        unit.y += (dy / distance) * step;
      }
    }
    this.syncBuildingActivity();
  }

  private reassignStoredTask(unit: WorldEntity): void {
    const signature = this.unitTasks.get(unit.id);
    const targetParts = signature?.split(':') ?? [];
    this.assignCharacterTaskRoute(unit, {
      name: unit.label ?? unit.id,
      profession: unit.kind,
      hairColor: unit.appearance?.hairColor ?? '#5a351f',
      shirtColor: unit.appearance?.shirtColor ?? '#a84432',
      pantsColor: unit.appearance?.pantsColor ?? '#46301f',
      gender: unit.appearance?.gender ?? 'male',
      isStreamer: unit.appearance?.isStreamer ?? false,
      x: unit.x,
      y: unit.y,
      task: targetParts[0] ?? 'idle',
      targetX: Number(targetParts[1] ?? 50),
      targetY: Number(targetParts[2] ?? 50),
      taskStartedAt: targetParts.slice(3).join(':'),
    });
  }

  private syncBuildingActivity(): void {
    const activeByBuilding = new Map<string, { count: number; task: string }>();
    for (const route of this.unitRoutes.values()) {
      if (!route.workingInside || !route.destinationBuildingId) continue;
      const current = activeByBuilding.get(route.destinationBuildingId);
      activeByBuilding.set(route.destinationBuildingId, {
        count: (current?.count ?? 0) + 1,
        task: route.task ?? current?.task ?? 'arbeit',
      });
    }
    for (const building of this.buildings) {
      const active = activeByBuilding.get(building.id);
      building.activeWorkers = active?.count;
      building.activeTask = active?.task;
      building.activity = active ? 'work' : undefined;
    }
  }

  private updateUnitAvoidance(deltaMs: number): void {
    const moving = this.units.filter((unit) =>
      !unit.hidden && (unit.activity === 'walk' || unit.activity === 'carry'),
    );
    const ease = Math.min(1, deltaMs / 180);
    for (const unit of this.units) {
      let targetX = 0;
      let targetY = 0;
      const neighbor = moving
        .filter((candidate) => candidate.id !== unit.id)
        .map((candidate) => ({
          unit: candidate,
          distance: Math.hypot(candidate.x - unit.x, candidate.y - unit.y),
        }))
        .filter((candidate) => candidate.distance < 0.56)
        .sort((a, b) => a.distance - b.distance)[0]?.unit;
      if (
        neighbor
        && this.isRoadPosition(unit)
        && this.isRoadPosition(neighbor)
        && !unit.hidden
        && (unit.activity === 'walk' || unit.activity === 'carry')
      ) {
        const side = unit.id.localeCompare(neighbor.id) < 0 ? -1 : 1;
        const route = this.unitRoutes.get(unit.id);
        const next = route?.path[route.waypointIndex];
        const directionX = next ? next.x - unit.x : neighbor.x - unit.x;
        const directionY = next ? next.y - unit.y : neighbor.y - unit.y;
        const directionLength = Math.max(0.001, Math.hypot(directionX, directionY));
        const passingOffset = 0.14 * side;
        targetX = (-directionY / directionLength) * passingOffset;
        targetY = (directionX / directionLength) * passingOffset;
      }
      unit.roadOffsetX = (unit.roadOffsetX ?? 0) + (targetX - (unit.roadOffsetX ?? 0)) * ease;
      unit.roadOffsetY = (unit.roadOffsetY ?? 0) + (targetY - (unit.roadOffsetY ?? 0)) * ease;
      if (Math.abs(unit.roadOffsetX) < 0.002) unit.roadOffsetX = 0;
      if (Math.abs(unit.roadOffsetY) < 0.002) unit.roadOffsetY = 0;
    }
  }

  private pathWithCurrentPosition(unit: WorldEntity, route: readonly Point[]): readonly Point[] {
    if (this.useVoxelNavigation && !this.isRoadPosition(unit)) {
      const road = this.nearestRoad(unit);
      unit.x = road.x;
      unit.y = road.y;
      unit.hidden = false;
    }
    const start = { x: unit.x, y: unit.y };
    const first = route[0];
    if (!first || Math.hypot(first.x - unit.x, first.y - unit.y) < 0.05) return route;
    return [start, ...route];
  }

  private pathWithBuildingExit(unit: WorldEntity, exitPath: readonly Point[], route: readonly Point[]): readonly Point[] {
    const start = { x: unit.x, y: unit.y };
    return [start, ...exitPath, ...route];
  }

  private appendBuildingEntrance(route: readonly Point[], building: WorldEntity, road: Point): readonly Point[] {
    if (!route.length) return route;
    const entrance = this.resolveBuildingEntrance(building);
    const approach = entrance.approach;
    const outsideDoor = approach[approach.length - 1];
    const last = route[route.length - 1];
    const withOutsideDoor = Math.hypot(last.x - outsideDoor.x, last.y - outsideDoor.y) < 0.05
      ? route
      : [...route, ...approach];
    void road;
    return [...withOutsideDoor, entrance.inside];
  }

  private buildingExitPath(building: WorldEntity, road: Point): readonly Point[] {
    return [...this.buildingDoorApproach(building, road)].reverse();
  }

  private buildingDoorApproach(building: WorldEntity, road: Point): readonly Point[] {
    void road;
    return this.resolveBuildingEntrance(building).approach;
  }

  private resolveBuildingEntrance(building: WorldEntity): BuildingEntrance {
    const anchor = this.navigationPoint(building);
    const cacheKey = [
      this.progressionLevel,
      building.id,
      anchor.x.toFixed(2),
      anchor.y.toFixed(2),
      this.runtimeRoadTiles.size,
    ].join(':');
    const cached = this.buildingEntrances.get(cacheKey);
    if (cached) return cached;

    // The connector targets the painted doorway, not the footprint centre.
    // The same asset metadata is used for generated buildings and later
    // construction, so every new road spur ends at the visible threshold.
    const { inside, outside } = this.visibleBuildingDoor(building);
    const roads = this.navigationRoadPoints();
    const road = roads.reduce((best, candidate) =>
      Math.hypot(candidate.x - outside.x, candidate.y - outside.y)
        < Math.hypot(best.x - outside.x, best.y - outside.y)
        ? candidate
        : best,
    );
    const approach = this.createDoorConnector(building, road, outside);
    const entrance = { inside, outside, road, approach };
    this.buildingEntrances.set(cacheKey, entrance);
    return entrance;
  }

  private visibleBuildingDoor(building: WorldEntity): { inside: Point; outside: Point } {
    const anchor = this.navigationPoint(building);
    const door = getBuildingDoorAnchor(building.kind);
    return {
      inside: { x: anchor.x + door.x, y: anchor.y + door.insideY },
      outside: { x: anchor.x + door.x, y: anchor.y + door.outsideY },
    };
  }

  private navigationRoadPoints(): readonly Point[] {
    if (this.useVoxelNavigation) return this.voxelRoadPoints();
    const roads = this.currentMap().tiles
      .filter((tile) => this.isRoadTile(tile))
      .map((tile) => ({ x: tile.x, y: tile.y }));
    return roads.length ? roads : [{ x: 16, y: 9 }];
  }

  private createDoorConnector(building: WorldEntity, road: Point, outside: Point): readonly Point[] {
    if (Math.hypot(road.x - outside.x, road.y - outside.y) < 0.05) return [outside];
    const horizontalFirst = [
      { x: outside.x, y: road.y },
      outside,
    ];
    const verticalFirst = [
      { x: road.x, y: outside.y },
      outside,
    ];
    const candidates = [horizontalFirst, verticalFirst]
      .map((points) => this.compactConnector(points))
      .sort((a, b) =>
        this.doorConnectorPenalty(building, road, a)
        - this.doorConnectorPenalty(building, road, b),
      );
    return candidates[0] ?? [outside];
  }

  private compactConnector(points: readonly Point[]): readonly Point[] {
    const compact: Point[] = [];
    for (const point of points) {
      const previous = compact[compact.length - 1];
      if (previous && Math.hypot(previous.x - point.x, previous.y - point.y) < 0.05) continue;
      compact.push(point);
    }
    return compact;
  }

  private doorConnectorPenalty(building: WorldEntity, road: Point, points: readonly Point[]): number {
    const route = [road, ...points];
    let penalty = 0;
    let length = 0;
    for (let index = 1; index < route.length; index += 1) {
      const from = route[index - 1];
      const to = route[index];
      const distance = Math.hypot(to.x - from.x, to.y - from.y);
      length += distance;
      const steps = Math.max(1, Math.ceil(distance / 0.35));
      for (let step = 1; step < steps; step += 1) {
        const progress = step / steps;
        const sample = {
          x: from.x + (to.x - from.x) * progress,
          y: from.y + (to.y - from.y) * progress,
        };
        const tile = this.currentMap().tiles.find((candidate) =>
          candidate.x === Math.round(sample.x) && candidate.y === Math.round(sample.y),
        );
        if (tile?.type === 'water' || tile?.type === 'mountain') penalty += 500;
        if (this.buildings.some((candidate) =>
          candidate.id !== building.id
          && Math.abs(this.navigationPoint(candidate).x - sample.x) < 1.15
          && Math.abs(this.navigationPoint(candidate).y - sample.y) < 0.95,
        )) penalty += 250;
      }
    }
    return penalty + length;
  }

  private nearestRoad(point: Point | WorldEntity): Point {
    if ('type' in point && point.type === 'building') {
      return this.resolveBuildingEntrance(point as WorldEntity).road;
    }
    const navigationPoint = this.navigationPoint(point);
    if (this.useVoxelNavigation) {
      return this.voxelRoadPoints().reduce((best, candidate) =>
        Math.hypot(candidate.x - navigationPoint.x, candidate.y - navigationPoint.y) <
        Math.hypot(best.x - navigationPoint.x, best.y - navigationPoint.y)
          ? candidate
          : best,
      );
    }
    const roads = this.currentMap().tiles.filter((tile) => this.isRoadTile(tile));
    let best = roads[0] ?? { x: Math.round(navigationPoint.x), y: Math.round(navigationPoint.y) };
    let bestDistance = Number.POSITIVE_INFINITY;
    for (const road of roads) {
      const distance = Math.abs(road.x - navigationPoint.x) + Math.abs(road.y - navigationPoint.y);
      if (distance < bestDistance) {
        best = road;
        bestDistance = distance;
      }
    }
    return { x: best.x, y: best.y };
  }

  private findRoadRoute(start: Point, target: Point): readonly Point[] {
    if (this.useVoxelNavigation) return this.findVoxelRoadRoute(start, target);
    const walkable = new Set(this.currentMap().tiles
      .filter((tile) => this.isRoadTile(tile))
      .map((tile) => `${tile.x}:${tile.y}`));
    const startKey = `${start.x}:${start.y}`;
    const targetKey = `${target.x}:${target.y}`;
    const queue: Point[] = [start];
    const cameFrom = new Map<string, string | undefined>([[startKey, undefined]]);
    const directions = [{ x: 1, y: 0 }, { x: -1, y: 0 }, { x: 0, y: 1 }, { x: 0, y: -1 }];

    while (queue.length) {
      const current = queue.shift();
      if (!current) break;
      const currentKey = `${current.x}:${current.y}`;
      if (currentKey === targetKey) break;
      for (const direction of directions) {
        const next = { x: current.x + direction.x, y: current.y + direction.y };
        const nextKey = `${next.x}:${next.y}`;
        if (!walkable.has(nextKey) || cameFrom.has(nextKey)) continue;
        cameFrom.set(nextKey, currentKey);
        queue.push(next);
      }
    }

    if (!cameFrom.has(targetKey)) {
      console.warn(`[Stream Kingdoms Movement] Kein sicherer Strassenpfad von ${startKey} nach ${targetKey}; direkte Wasserquerung blockiert.`);
      return [];
    }
    const out: Point[] = [];
    let cursor: string | undefined = targetKey;
    while (cursor) {
      const [x, y] = cursor.split(':').map(Number);
      out.unshift({ x, y });
      cursor = cameFrom.get(cursor);
    }
    return out;
  }

  private currentMap(): MapData {
    if (this.runtimeMap) return this.runtimeMap;
    if (!this.runtimeRoadTiles.size) return this.layout.map;
    const tiles = this.layout.map.tiles.map((tile) => this.runtimeRoadTiles.get(this.tileKey(tile)) ?? tile);
    this.runtimeMap = { ...this.layout.map, tiles };
    return this.runtimeMap;
  }

  private isRoadTile(tile: MapTile | undefined): boolean {
    return tile?.type === 'road' || tile?.variant === 'bridge_h' || tile?.variant === 'bridge_v';
  }

  private isRoadPosition(point: Point): boolean {
    if (this.useVoxelNavigation) {
      return this.voxelRoadPoints().some((road) => Math.hypot(road.x - point.x, road.y - point.y) <= 0.42);
    }
    const map = this.currentMap();
    const x = Math.round(point.x);
    const y = Math.round(point.y);
    return map.tiles.some((tile) =>
      this.isRoadTile(tile)
      && Math.abs(tile.x - point.x) <= 0.55
      && Math.abs(tile.y - point.y) <= 0.55,
    ) || this.isRoadTile(map.tiles.find((tile) => tile.x === x && tile.y === y));
  }

  private findVoxelRoadRoute(start: Point, target: Point): readonly Point[] {
    const startRoad = this.nearestVoxelRoadPoint(start);
    const targetRoad = this.nearestVoxelRoadPoint(target);
    const startKey = pointKey(startRoad);
    const targetKey = pointKey(targetRoad);
    const queue: Point[] = [startRoad];
    const cameFrom = new Map<string, string | undefined>([[startKey, undefined]]);

    while (queue.length) {
      const current = queue.shift();
      if (!current) break;
      const currentKey = pointKey(current);
      if (currentKey === targetKey) break;
      for (const nextKey of this.voxelRoadGraph.adjacency.get(currentKey) ?? []) {
        if (cameFrom.has(nextKey)) continue;
        cameFrom.set(nextKey, currentKey);
        const next = this.voxelRoadGraph.byKey.get(nextKey);
        if (next) queue.push(next);
      }
    }

    if (!cameFrom.has(targetKey)) return [];
    const route: Point[] = [];
    let cursor: string | undefined = targetKey;
    while (cursor) {
      const point = this.voxelRoadGraph.byKey.get(cursor);
      if (point) route.unshift(point);
      cursor = cameFrom.get(cursor);
    }
    return smoothRoadCenterline(route);
  }

  private nearestVoxelRoadPoint(point: Point): Point {
    return this.voxelRoadPoints().reduce((best, candidate) =>
      Math.hypot(candidate.x - point.x, candidate.y - point.y) <
      Math.hypot(best.x - point.x, best.y - point.y)
        ? candidate
        : best,
    );
  }

  private voxelRoadPoints(): readonly Point[] {
    return this.voxelRoadGraph.points;
  }

  private navigationPoint(point: Point | WorldEntity): Point {
    return { x: point.x, y: point.y };
  }

  private findCompletedBuildingPosition(
    requested: Point,
    existing: readonly WorldEntity[] = this.buildings,
    footprint: { readonly width: number; readonly height: number } = { width: 2, height: 2 },
  ): Point {
    const map = this.currentMap();
    const origin = {
      x: Math.max(2, Math.min(map.width - 3, requested.x)),
      y: Math.max(2, Math.min(map.height - 3, requested.y)),
    };
    const candidates: Point[] = [origin];
    for (let radius = 1; radius <= 10; radius += 1) {
      for (let y = -radius; y <= radius; y += 1) {
        for (let x = -radius; x <= radius; x += 1) {
          if (Math.max(Math.abs(x), Math.abs(y)) !== radius) continue;
          candidates.push({ x: Math.round(origin.x + x), y: Math.round(origin.y + y) });
        }
      }
    }
    const nearby = candidates.find((candidate) => this.isBuildingSiteClear(candidate, existing, footprint));
    if (nearby) return nearby;
    const fallback = map.tiles
      .filter((tile) => tile.type === 'grass')
      .sort((a, b) =>
        Math.hypot(a.x - origin.x, a.y - origin.y) - Math.hypot(b.x - origin.x, b.y - origin.y),
      )
      .find((tile) => this.isBuildingSiteClear(tile, existing, footprint));
    return fallback ?? origin;
  }

  private isBuildingSiteClear(
    point: Point,
    existing: readonly WorldEntity[] = this.buildings,
    footprint: { readonly width: number; readonly height: number } = { width: 2, height: 2 },
  ): boolean {
    const map = this.currentMap();
    const tileLookup = new Map(map.tiles.map((tile) => [this.tileKey(tile), tile]));
    const halfWidth = Math.ceil(footprint.width / 2);
    const halfHeight = Math.ceil(footprint.height / 2);
    for (let y = Math.round(point.y) - halfHeight; y <= Math.round(point.y) + halfHeight; y += 1) {
      for (let x = Math.round(point.x) - halfWidth; x <= Math.round(point.x) + halfWidth; x += 1) {
        const tile = tileLookup.get(`${x}:${y}`);
        if (!tile || !['grass', 'field', 'workyard', 'shore'].includes(tile.type)) return false;
      }
    }
    if (existing.some((building) => {
      const existingFootprint = building.building?.footprint ?? { width: 2, height: 2 };
      const horizontalClearance = (footprint.width + existingFootprint.width) / 2 + 1.35;
      const verticalClearance = (footprint.height + existingFootprint.height) / 2 + 1.05;
      return Math.abs(building.x - point.x) < horizontalClearance
        && Math.abs(building.y - point.y) < verticalClearance;
    })) return false;
    return !this.decorations.some((decoration) =>
      Math.abs(decoration.x - point.x) < halfWidth + 0.8
      && Math.abs(decoration.y - point.y) < halfHeight + 0.7,
    );
  }

  private ensureBuildingRoadConnections(buildings: readonly WorldEntity[]): void {
    let addedRoadTiles = 0;
    for (const building of buildings) {
      if (building.kind.startsWith('wall_')) continue;
      const visibleDoor = this.visibleBuildingDoor(building).outside;
      const entrance = {
        x: Math.round(visibleDoor.x),
        y: Math.round(visibleDoor.y),
      };
      if (this.isRoadPosition(entrance)) continue;
      const path = this.findDryConnectionToRoad(entrance);
      if (!path.length) {
        console.warn(`[Stream Kingdoms Roads] Kein sicherer Straßenanschluss für ${building.id} bei ${entrance.x}:${entrance.y}.`);
        continue;
      }
      for (const point of path.slice(0, -1)) {
        const key = this.tileKey(point);
        const original = this.layout.map.tiles.find((tile) => tile.x === point.x && tile.y === point.y);
        if (!original || this.isRoadTile(original) || this.runtimeRoadTiles.has(key)) continue;
        this.runtimeRoadTiles.set(key, { x: point.x, y: point.y, type: 'road', variant: 'auto' });
        addedRoadTiles += 1;
      }
      this.runtimeMap = undefined;
    }
    if (addedRoadTiles > 0) {
      console.info(`[Stream Kingdoms Roads] ${addedRoadTiles} Straßenfelder für Gebäudeanschlüsse ergänzt.`);
    }
    this.voxelRoadGraph = sampleRoadMap(this.currentMap());
  }

  private findDryConnectionToRoad(start: Point): readonly Point[] {
    const map = this.currentMap();
    const tiles = new Map(map.tiles.map((tile) => [this.tileKey(tile), tile]));
    const startKey = this.tileKey(start);
    const queue: Point[] = [start];
    const cameFrom = new Map<string, string | undefined>([[startKey, undefined]]);
    const directions = [{ x: 1, y: 0 }, { x: -1, y: 0 }, { x: 0, y: 1 }, { x: 0, y: -1 }];
    let targetKey: string | undefined;

    while (queue.length) {
      const current = queue.shift();
      if (!current) break;
      const currentKey = this.tileKey(current);
      const tile = tiles.get(currentKey);
      if (currentKey !== startKey && this.isRoadTile(tile)) {
        targetKey = currentKey;
        break;
      }
      for (const direction of directions) {
        const next = { x: current.x + direction.x, y: current.y + direction.y };
        const nextKey = this.tileKey(next);
        const nextTile = tiles.get(nextKey);
        if (!nextTile || cameFrom.has(nextKey)) continue;
        if (!this.isRoadTile(nextTile) && !['grass', 'field', 'workyard', 'shore'].includes(nextTile.type)) continue;
        if (this.isRoadCellBlocked(next)) continue;
        cameFrom.set(nextKey, currentKey);
        queue.push(next);
      }
    }

    if (!targetKey) return [];
    const path: Point[] = [];
    let cursor: string | undefined = targetKey;
    while (cursor) {
      const [x, y] = cursor.split(':').map(Number);
      path.unshift({ x, y });
      cursor = cameFrom.get(cursor);
    }
    return path;
  }

  private isRoadCellBlocked(point: Point): boolean {
    const blockedByBuilding = this.buildings.some((building) => {
      const anchor = this.navigationPoint(building);
      const visibleDoor = this.visibleBuildingDoor(building).outside;
      const entrance = { x: Math.round(visibleDoor.x), y: Math.round(visibleDoor.y) };
      if (point.x === entrance.x && point.y === entrance.y) return false;
      return Math.abs(anchor.x - point.x) < 1
        && Math.abs(anchor.y - point.y) < 1;
    });
    if (blockedByBuilding) return true;
    return this.decorations.some((decoration) =>
      Math.abs(decoration.x - point.x) < 0.6
      && Math.abs(decoration.y - point.y) < 0.6,
    );
  }

  private tileKey(point: Point): string {
    return `${point.x}:${point.y}`;
  }

  private resourceModifier(resource: keyof ResourceState): number {
    const production = this.seasonManager.getState().production;
    if (resource === 'wood') return production.wood;
    if (resource === 'stone') return production.stone;
    if (resource === 'food') return production.food;
    return production.gold;
  }
}

function resolveFacing(dx: number, dy: number): WorldEntity['facing'] {
  if (Math.abs(dx) > Math.abs(dy)) return dx >= 0 ? 'east' : 'west';
  return dy >= 0 ? 'south' : 'north';
}

function sampleRoadGraph(roadGraph: RoadGraph): NavigationRoadGraph {
  const byKey = new Map<string, Point>();
  const adjacencySets = new Map<string, Set<string>>();

  const register = (point: Point): string => {
    const rounded = {
      x: Math.round(point.x * 100) / 100,
      y: Math.round(point.y * 100) / 100,
    };
    const key = pointKey(rounded);
    byKey.set(key, rounded);
    if (!adjacencySets.has(key)) adjacencySets.set(key, new Set());
    return key;
  };

  const connect = (fromKey: string, toKey: string): void => {
    if (fromKey === toKey) return;
    adjacencySets.get(fromKey)?.add(toKey);
    adjacencySets.get(toKey)?.add(fromKey);
  };

  for (const node of roadGraph.nodes) register(node);

  for (const edge of roadGraph.edges) {
    let previousKey: string | undefined;
    for (let pathIndex = 0; pathIndex < edge.path.length - 1; pathIndex += 1) {
      const from = edge.path[pathIndex];
      const to = edge.path[pathIndex + 1];
      const distance = Math.hypot(to.x - from.x, to.y - from.y);
      const steps = Math.max(1, Math.ceil(distance / 0.2));
      for (let step = 0; step <= steps; step += 1) {
        if (pathIndex > 0 && step === 0) continue;
        const progress = step / steps;
        const currentKey = register({
          x: from.x + (to.x - from.x) * progress,
          y: from.y + (to.y - from.y) * progress,
        });
        if (previousKey) connect(previousKey, currentKey);
        previousKey = currentKey;
      }
    }
    if (edge.path.length === 1) register(edge.path[0]);
  }

  return {
    points: [...byKey.values()],
    byKey,
    adjacency: new Map([...adjacencySets].map(([key, neighbors]) => [key, [...neighbors]])),
  };
}

function sampleRoadMap(map: MapData): NavigationRoadGraph {
  const roads = map.tiles.filter((tile) =>
    tile.type === 'road'
    || (tile.type === 'water' && (tile.variant === 'bridge_h' || tile.variant === 'bridge_v')),
  );
  const roadKeys = new Set(roads.map((tile) => `${tile.x}:${tile.y}`));
  const edges: RoadGraph['edges'][number][] = [];
  for (const tile of roads) {
    for (const direction of [{ x: 1, y: 0 }, { x: 0, y: 1 }]) {
      const neighbor = { x: tile.x + direction.x, y: tile.y + direction.y };
      if (!roadKeys.has(`${neighbor.x}:${neighbor.y}`)) continue;
      edges.push({
        from: `road-${tile.x}-${tile.y}`,
        to: `road-${neighbor.x}-${neighbor.y}`,
        kind: tile.type === 'water' ? 'bridge' : 'main',
        path: [{ x: tile.x, y: tile.y }, neighbor],
      });
    }
  }
  return sampleRoadGraph({
    nodes: roads.map((tile) => ({
      id: `road-${tile.x}-${tile.y}`,
      x: tile.x,
      y: tile.y,
      kind: tile.type === 'water' ? 'bridge' : 'main',
    })),
    edges,
  });
}

function smoothRoadCenterline(route: readonly Point[]): readonly Point[] {
  if (route.length < 3) return route;
  const smoothed: Point[] = [route[0]];
  const radius = 0.28;
  for (let index = 1; index < route.length - 1; index += 1) {
    const previous = route[index - 1];
    const corner = route[index];
    const next = route[index + 1];
    const incoming = normalizeVector(corner.x - previous.x, corner.y - previous.y);
    const outgoing = normalizeVector(next.x - corner.x, next.y - corner.y);
    const straight = Math.abs(incoming.x - outgoing.x) < 0.01
      && Math.abs(incoming.y - outgoing.y) < 0.01;
    if (straight) {
      smoothed.push(corner);
      continue;
    }
    const entry = { x: corner.x - incoming.x * radius, y: corner.y - incoming.y * radius };
    const exit = { x: corner.x + outgoing.x * radius, y: corner.y + outgoing.y * radius };
    smoothed.push(entry);
    for (const progress of [0.25, 0.5, 0.75, 1]) {
      const inverse = 1 - progress;
      smoothed.push({
        x: inverse * inverse * entry.x + 2 * inverse * progress * corner.x + progress * progress * exit.x,
        y: inverse * inverse * entry.y + 2 * inverse * progress * corner.y + progress * progress * exit.y,
      });
    }
  }
  smoothed.push(route[route.length - 1]);
  return smoothed;
}

function normalizeVector(x: number, y: number): Point {
  const length = Math.max(0.001, Math.hypot(x, y));
  return { x: x / length, y: y / length };
}

function pointKey(point: Point): string {
  return `${point.x.toFixed(2)}:${point.y.toFixed(2)}`;
}
